import { ArticlePositionLayerData } from '@3d-scene/layer/creator/article-position-layer-data';
import { BaseLayerElementCreator } from '@3d-scene/layer/creator/base-layer-element-creator';
import { BmkLayerElementCreator } from '@3d-scene/layer/creator/bmk-layer-element-creator';
import { LayerDataHandlers, LayerName } from '@3d-scene/manager/layer-manager';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { ScreenShotService } from '@3d-scene/service/screen-shot.service';
import { assertExhaustiveSwitch } from '@shared/util/ts-utils';
import { ProjectModel } from '@project/model/project.model';
import { ArticleNumberLayerElementCreator } from '@3d-scene/layer/creator/article-number-layer-element-creator';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { DrillingHolesLayerElementCreator } from '@3d-scene/layer/creator/drilling-holes-layer-element-creator';
import { RulerLayerElementCreator } from '../layer/creator/ruler-layer-element-creator';
import { ProjectHelper } from '@project/helper/project.helper';
import { ArticlePositionLayerElementCreator } from '../layer/creator/article-position-layer-element-creator';
import { SceneManager } from './scene.manager';
import { UserModel } from '@user/model/user.model';
import { Injectable } from '@angular/core';
import { ProjectDimensionsService } from '@project/components/project/service/project-dimensions.service';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { DimensionLayerElementCreator } from '../layer/creator/dimension-layer-element-creator';
import { ArticleContainerUtils } from '../model3d/util/article-container-utils';
import { ContainerService } from '../model3d/container.service';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { ProjectService } from '@project/components/import-export/service/project.service';
import { GlobalArticleContainerUtils } from '../model3d/util/global-article-container-utils';
import { UserModelService } from '@user/model/user.model.service';
import { Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LayerElementCreatorFactory {

  constructor(
    private userModel: UserModel,
    private projectModel: ProjectModel,
    private mountingRailHelper: MountingRailHelper,
    private projectDimensionsService: ProjectDimensionsService,
    private projectHelper: ProjectHelper,
    private sceneManager: SceneManager,
    private localeResourceManager: LocaleResourceManager,
    private projectContentPresenter: ProjectContentPresenter,
    private containerService: ContainerService,
    private articleContainerUtils: ArticleContainerUtils,
    private articleUsageService: ArticleUsageService,
    private projectService: ProjectService,
    private globalArticleContainerUtils: GlobalArticleContainerUtils,
    private screenShotService: ScreenShotService,
    private articleConnectionService: ArticleConnectionService,
    private userModelService: UserModelService,
  ) {
  }

  public getCreator(layerName: LayerName,  dataHandlers: LayerDataHandlers): BaseLayerElementCreator {
    switch (layerName) {
      case "dimensions":
        return this.getDimensionLayerElementCreator();
      case "articleNumbers":
        return this.getArticleNumberLayerElementCreator();
      case "bmk":
        return this.getBmkLayerElementCreator();
      case "drillingHoles":
        return this.getDrillingHolesLayerElementCreator();
      case 'ruler':
        return this.getRulerLayerElementCreator();
      case "articlePosition":
        return this.getArticlePositionLayerElementCreator(dataHandlers.articlePosition);
      default:
        assertExhaustiveSwitch(layerName);
    }
  }

  public subscribeToLayerUpdates(layerName: LayerName, updateCallback: () => void): Subscription | undefined {
    switch (layerName) {
      case 'articleNumbers':
        return this.subscribeToArticleNumberLayerUpdates(updateCallback);
      case 'dimensions':
      case 'bmk':
      case 'drillingHoles':
      case 'ruler':
      case 'articlePosition':
      default:
        return undefined;
    }
  }

  private subscribeToArticleNumberLayerUpdates(updateCallback: () => void): Subscription {
    const subs = new Subscription();
    subs.add(this.userModelService.articleNumberMappingsChanged$.subscribe(updateCallback));
    subs.add(this.projectContentPresenter.showCustomArticleNumber$.subscribe(updateCallback));
    return subs;
  }


  private getDimensionLayerElementCreator(): DimensionLayerElementCreator {
    return new DimensionLayerElementCreator(
      this.projectModel,
      this.containerService,
      this.localeResourceManager,
      this.sceneManager,
      this.projectDimensionsService,
      this.mountingRailHelper,
      this.articleContainerUtils,
      this.articleUsageService,
      this.projectService,
      this.globalArticleContainerUtils,
    );
  }

  private getArticleNumberLayerElementCreator(): ArticleNumberLayerElementCreator {
    return new ArticleNumberLayerElementCreator(
      this.projectModel,
      this.userModel,
      this.projectContentPresenter,
      this.mountingRailHelper,
      this.projectHelper,
      this.containerService
    );
  }

  private getBmkLayerElementCreator(): BmkLayerElementCreator {
    return new BmkLayerElementCreator(
      this.projectModel,
      this.mountingRailHelper,
      this.containerService,
      this.articleConnectionService,
    );
  }

  private getDrillingHolesLayerElementCreator(): DrillingHolesLayerElementCreator {
    return new DrillingHolesLayerElementCreator(
      this.containerService,
      this.localeResourceManager,
      this.projectModel,
      this.sceneManager,
      this.mountingRailHelper,
      this.articleContainerUtils,
      this.articleUsageService,
      this.projectDimensionsService,
      this.screenShotService,
    );
  }

  private getRulerLayerElementCreator(): RulerLayerElementCreator {
    return new RulerLayerElementCreator(
      this.mountingRailHelper,
      this.projectModel,
    );
  }

  private getArticlePositionLayerElementCreator(articlePositionLayerData: ArticlePositionLayerData): ArticlePositionLayerElementCreator {
    return new ArticlePositionLayerElementCreator(this.projectModel, articlePositionLayerData);
  }
}
